import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useRouteParams } from '@/context/routeParamsContext';
import { useModal } from '@/context/modalContext';
import { cardArtMapping } from '@/constants/productMapping';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { getCardArtUrl, cn } from '@/helpers/utils';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { IconChevronDown } from '@americanexpress/dls-icons';
import { useSelectedProductFromUserWallet } from '@/hooks/memberHooks';
import LoungeAccessProductDescription from './LoungeAccessProductDescription';
import { modalTypes } from './modals/constants';

const InlineCardCalloutNotification = ({
  className,
  hideImage = false,
  variant = 'default',
}) => {
  const isLoungeDetailsVariant = variant === 'loungeDetails';
  const { product } = useRouteParams();
  const { locale } = useSelector(getLocaleData);
  const { customTrack } = useAnalyticsContext();
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const urlSpecifiedProductFromUsersWallet = useSelectedProductFromUserWallet(
    product,
    locale
  );
  const imgSrc = getCardArtUrl({
    baseOneCmsImgUrl,
    product: cardArtMapping[product],
    locale,
  });
  const { setModalName } = useModal();

  const handleToggleModal = (event) => {
    setModalName(modalTypes.SELECT_CARD_MODAL);
    customTrack({
      event,
      detail: 'card call out notification',
      element: 'change card button',
    });
  };

  return (
    <button
      type="button"
      className={cn(
        'tw-flex tw-w-fit tw-items-center tw-text-xl tw-font-medium tw-text-blue-bright-600 focus-within_tw-rounded-sm focus-within_tw-outline focus-within_tw-outline-2 focus-within_tw-outline-blue-bright-600 md_tw-text-2xl',
        className
      )}
      onClick={handleToggleModal}
    >
      {!hideImage && (
        <img
          src={urlSpecifiedProductFromUsersWallet?.cardArtUrl || imgSrc}
          alt=""
          className="tw-h-6 tw-w-9dot5"
        />
      )}

      <div
        className={cn(
          'tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-left first-child_tw-flex-1 first-child_tw-whitespace-normal first-child_tw-break-words',
          isLoungeDetailsVariant && 'tw-gap-1',
          !hideImage && 'tw-pl-2'
        )}
      >
        <LoungeAccessProductDescription
          locale={locale}
          product={product}
          customMessage="{productTitle} ({countryCode})" // No need to localize as this is just productName and code
        />
        <span>
          <IconChevronDown
            size="xs"
            isFilled={true}
            color="inherit"
            className={cn(
              'tw-fill-blue-bright-600',
              !isLoungeDetailsVariant && 'md_tw-hidden'
            )}
          />
          {!isLoungeDetailsVariant && (
            <IconChevronDown
              size="sm"
              isFilled={true}
              color="inherit"
              className="tw-hidden tw-fill-blue-bright-600 md_tw-block"
            />
          )}
        </span>
      </div>
    </button>
  );
};

InlineCardCalloutNotification.propTypes = {
  className: PropTypes.string,
  hideImage: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'loungeDetails']),
};

export default InlineCardCalloutNotification;
